/**
 * كتالوج التوظيف.
 *
 * التوظيف رحلةُ شخصٍ لا سجلُّ بيانات: يتقدّم، فيُفرز، فيُكلَّم، فيُقابَل،
 * فيُعرض عليه، فيُعيَّن — أو يقف عند مرحلةٍ ويُقال له لماذا. وما دون ذلك
 * صناديقُ بريدٍ تُنسى فيها الناس.
 *
 * المراحلُ هنا لا في قاعدة البيانات: عمودُ `stage` نصٌّ، والمعنى يُقرأ من
 * هذا الملفّ. فإضافة مرحلةٍ لا تحتاج ترحيلًا، وحذفُ مرحلةٍ لا يُفقد ما
 * وقف عندها من الطلبات — يبقى المفتاح مكتوبًا ويُعرض باسمه الخام.
 */
import type { Labelled } from './common.js';

/* ==========================================================================
   نوع التعاقد
   ========================================================================== */

export const EMPLOYMENT_TYPES = [
  'FULL_TIME',
  'PART_TIME',
  'CONTRACT',
  'INTERN',
  'FREELANCE',
] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

export const EMPLOYMENT_TYPE_DEFS: readonly Labelled<EmploymentType>[] = [
  { key: 'FULL_TIME', label: 'دوام كامل', kind: 'success' },
  { key: 'PART_TIME', label: 'دوام جزئي', kind: 'info' },
  { key: 'CONTRACT', label: 'عقد مؤقّت', kind: 'warning' },
  { key: 'INTERN', label: 'تدريب', kind: 'neutral' },
  { key: 'FREELANCE', label: 'عمل حرّ', kind: 'brand' },
] as const;

export const EMPLOYMENT_TYPE_MAP = Object.fromEntries(
  EMPLOYMENT_TYPE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<EmploymentType, Labelled<EmploymentType>>>;

/* ==========================================================================
   المؤهّل المطلوب
   ========================================================================== */

/**
 * أدنى مؤهّلٍ يُقبل.
 *
 * سلّمٌ مرتّبٌ لا قائمةُ أسماء: «بكالوريوس» تعني بكالوريوسَ فما فوق، فمن
 * يحمل ماجستيرًا لا يُردّ لأنّ خانتَه لا تطابق الخانة. ولذلك لكلّ درجةٍ
 * رتبةٌ رقميّة — ولولاها لاحتاج كلُّ إعلانٍ أن يعدّ المؤهّلات المقبولة.
 *
 * و«لا يُشترط» درجةٌ في السلّم لا غيابُ قيمة: فرقٌ بين إعلانٍ قال «لا
 * يُشترط مؤهّل» وإعلانٍ نسي أن يقول شيئًا — الأوّلُ قرارٌ والثاني نقص.
 */
export const EDUCATION_LEVELS = [
  'NONE',
  'SECONDARY',
  'DIPLOMA',
  'BACHELOR',
  'MASTER',
  'PHD',
] as const;
export type EducationLevel = (typeof EDUCATION_LEVELS)[number];

export interface EducationLevelDef extends Labelled<EducationLevel> {
  /** رتبتُه في السلّم — الأعلى يقبل من دونه. */
  rank: number;
}

export const EDUCATION_LEVEL_DEFS: readonly EducationLevelDef[] = [
  { key: 'NONE', label: 'لا يُشترط', kind: 'neutral', rank: 0 },
  { key: 'SECONDARY', label: 'ثانوية عامة', kind: 'neutral', rank: 1 },
  { key: 'DIPLOMA', label: 'دبلوم', kind: 'info', rank: 2 },
  { key: 'BACHELOR', label: 'بكالوريوس', kind: 'brand', rank: 3 },
  { key: 'MASTER', label: 'ماجستير', kind: 'success', rank: 4 },
  { key: 'PHD', label: 'دكتوراه', kind: 'success', rank: 5 },
] as const;

export const EDUCATION_LEVEL_MAP = Object.fromEntries(
  EDUCATION_LEVEL_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<EducationLevel, EducationLevelDef>>;

/* ==========================================================================
   منشور التعيين
   ========================================================================== */

/** ما يحتاجه المنشور من ملفّ الوظيفة. */
export interface AnnouncementSource {
  companyName: string;
  title: string;
  department: string | null;
  employmentLabel: string;
  workModeLabel: string | null;
  location: string | null;
  description: string | null;
  duties: readonly string[];
  techRequirements: readonly string[];
  benefits: readonly string[];
  experienceMin: number | null;
  educationLabel: string | null;
  salary: { min: number | null; max: number | null } | null;
  closesOn: string | null;
  applyUrl: string;
}

/** أقصى طولٍ للمنشور — أطولُ من هذا لا يُنشر على أيّ منصّة. */
export const MAX_ANNOUNCEMENT = 4000;

/** كم بندًا يُؤخذ من كلّ قائمةٍ إلى المنشور — والباقي في الصفحة. */
const ANNOUNCEMENT_BULLETS = 5;

function moneyText(n: number): string {
  return n.toLocaleString('ar-EG');
}

/**
 * مسوّدةُ منشورِ تعيين.
 *
 * ## ولماذا مسوّدةٌ لا نصٌّ نهائيّ
 *
 * المنشورُ نصٌّ إعلانيٌّ يُكتب بلغةِ من يكتبه: شركةٌ تخاطب مهندسين بلغةٍ
 * وأخرى تخاطب باعةً بلغة. فيُولَّد مرّةً ليكون بدايةً — تُرتَّب فيه
 * الحقائقُ بالترتيب الذي يقرأ به الناسُ إعلانَ وظيفة — ثمّ يُحرَّر ويُحفظ
 * كما حُرّر. وتوليدٌ في كلّ عرضٍ كان يمحو ما كتبه من صاغه.
 *
 * ## والترتيبُ ليس عشوائيًّا
 *
 * المسمّى، فالمكانُ والنمط، فالراتبُ إن أُعلن، فماذا ستعمل، فما المطلوب،
 * فما نقدّمه، فالرابط. وهو ترتيبُ أسئلةِ من يقرأ إعلانًا: «ما هي؟ أين؟
 * بكم؟ ماذا أفعل؟ هل أنطبق؟ ماذا أكسب؟ كيف أتقدّم؟» — وإعلانٌ يبدأ
 * بتاريخ الشركة يُمرَّر عليه.
 */
export function draftAnnouncement(src: AnnouncementSource): string {
  const lines: string[] = [];

  lines.push(`نبحث عن: ${src.title}`);
  lines.push(`${src.companyName}`);
  lines.push('');

  const meta = [
    src.department,
    src.employmentLabel,
    src.workModeLabel,
    src.location,
  ].filter(Boolean);
  if (meta.length > 0) lines.push(meta.join(' · '));

  if (src.salary && (src.salary.min !== null || src.salary.max !== null)) {
    const { min, max } = src.salary;
    const text =
      min !== null && max !== null
        ? `${moneyText(min)} — ${moneyText(max)}`
        : min !== null
          ? `من ${moneyText(min)}`
          : `حتى ${moneyText(max!)}`;
    lines.push(`الراتب: ${text}`);
  }

  const conditions = [
    src.experienceMin !== null ? `خبرة ${src.experienceMin} سنة فأكثر` : null,
    src.educationLabel,
  ].filter(Boolean);
  if (conditions.length > 0) lines.push(conditions.join(' · '));

  if (src.description) {
    lines.push('');
    lines.push(src.description);
  }

  const section = (title: string, items: readonly string[]) => {
    if (items.length === 0) return;
    lines.push('');
    lines.push(`${title}:`);
    for (const x of items.slice(0, ANNOUNCEMENT_BULLETS)) lines.push(`- ${x}`);
    /*
     * ما زاد لا يُقصّ صامتًا.
     *
     * منشورٌ يذكر خمسًا من عشرة ثمّ يسكت يبدو كأنّ الشركةَ لا تطلب غيرَها،
     * فيتقدّم من لا ينطبق عليه الباقي. والسطرُ يقول إنّ في الصفحة مزيدًا.
     */
    if (items.length > ANNOUNCEMENT_BULLETS) {
      lines.push(`- والمزيد في صفحة الوظيفة`);
    }
  };

  section('ماذا ستعمل', src.duties);
  section('المطلوب', src.techRequirements);
  section('ما نقدّمه', src.benefits);

  lines.push('');
  if (src.closesOn) lines.push(`التقديم حتى ${src.closesOn}`);
  lines.push('للتقديم:');
  lines.push(src.applyUrl);

  return lines.join('\n').slice(0, MAX_ANNOUNCEMENT);
}

/* ==========================================================================
   حدود ملفّ الوظيفة
   ========================================================================== */

/** أقصى عددِ بنودٍ في قائمةٍ واحدةٍ من قوائم ملفّ الوظيفة. */
export const MAX_JOB_BULLETS = 20;

/**
 * ما ينقص ملفَّ الوظيفة.
 *
 * لا يمنع الحفظ: وظيفةٌ تُكتب على دفعاتٍ حالةٌ طبيعيّة، ومنعُ الحفظ حتى
 * تكتمل يجعل من يبدأها يفقد ما كتب. ولكنّه يُعرض قبل النشر — لأنّ إعلانًا
 * بلا مهامَّ ولا متطلّباتٍ يجلب متقدّمين لا يعرفون ما تقدّموا له، ثمّ
 * يُسقَطون في أوّل مقابلةٍ فتُهدر ساعاتُ الطرفين.
 */
export interface JobFileShape {
  description: string | null;
  duties: readonly string[];
  techRequirements: readonly string[];
  softRequirements: readonly string[];
  benefits: readonly string[];
  location: string | null;
  salaryMin: number | null;
  salaryMax: number | null;
  positionId: string | null;
  deptKey: string | null;
  closesOn: string | Date | null;
}

export interface JobGap {
  field: string;
  label: string;
  /** هل هو نقصٌ يُعطّل النشرَ أم نقصٌ يُقال ويُتجاوَز. */
  blocking: boolean;
  why: string;
}

/**
 * ما ينقص هذا الملفَّ، وأيُّه يمنع النشر.
 *
 * المانعُ هو ما لا يستقيم إعلانٌ بدونه: من يقرأ الإعلانَ يحتاج أن يعرف
 * ماذا سيعمل وما المطلوبُ منه. وما سواه يُقال ولا يمنع — شركةٌ لا تعلن
 * الراتبَ لها أسبابُها، ووظيفةٌ بلا تاريخِ إغلاقٍ مفتوحةٌ حتى تُغلق بيد.
 */
export function jobGaps(job: JobFileShape): JobGap[] {
  const gaps: JobGap[] = [];
  const add = (field: string, label: string, blocking: boolean, why: string) =>
    gaps.push({ field, label, blocking, why });

  if (!job.description || job.description.trim().length < 30) {
    add(
      'description',
      'الوصف الوظيفي',
      true,
      'من يقرأ الإعلانَ لا يعرف ماذا سيعمل، فيتقدّم على الاسم وحدَه.',
    );
  }
  if (job.duties.length === 0) {
    add(
      'duties',
      'المهامّ الرئيسية',
      true,
      'المهامُّ هي ما يُقاس به المرشّحُ في المقابلة — وبلا قائمةٍ يُقاس بالانطباع.',
    );
  }
  if (job.techRequirements.length === 0) {
    add(
      'techRequirements',
      'المتطلّبات التقنية',
      true,
      'بلا متطلّباتٍ معلنةٍ يتقدّم من لا تنطبق عليه، فتُهدر ساعاتُ الفرز.',
    );
  }
  if (job.softRequirements.length === 0) {
    add(
      'softRequirements',
      'المتطلّبات الشخصية',
      false,
      'يُقاس بها في المقابلة — وبلا كتابتها يختلف مُقابلان في المعيار.',
    );
  }
  if (job.salaryMin === null && job.salaryMax === null) {
    add(
      'salary',
      'نطاق الراتب',
      false,
      'يُكتب ولو لم يُعلَن: منه يُملأ عرضُ العمل، وبه تُقارن الشركةُ ما تدفع بالسوق.',
    );
  }
  if (job.benefits.length === 0) {
    add(
      'benefits',
      'المزايا',
      false,
      'ما يُعرض غيرَ الراتب — وهو ما يرجّح كفّةَ عرضٍ على عرض.',
    );
  }
  if (!job.location) {
    add('location', 'مكان العمل', false, 'المتقدّمُ يسأل عنه قبل أن يسأل عن الراتب.');
  }
  if (!job.deptKey) {
    add(
      'deptKey',
      'القسم',
      false,
      'بلا قسمٍ لا يظهر من يُعيَّن في شاشة فريقه بعد التعيين.',
    );
  }
  if (!job.positionId) {
    add(
      'positionId',
      'الوصف الوظيفي المعتمَد',
      false,
      'بلا ربطٍ به لا يُعرف هذا الشاغرُ من المعتمَد في تخطيط القوى العاملة.',
    );
  }
  return gaps;
}

/** هل يستقيم نشرُ هذا الملفّ. */
export function jobReady(job: JobFileShape): boolean {
  return jobGaps(job).every((g) => !g.blocking);
}

/* ==========================================================================
   مصدر المرشّح
   ========================================================================== */

/**
 * من أين جاء.
 *
 * ليس حقلًا للأرشيف: هو الذي يقول أيّ قناةٍ تستحقّ الإنفاق. وحين يكون
 * نصف من عُيّنوا جاؤوا بترشيحٍ من موظّف، فذاك رقمٌ يغيّر ميزانيةَ الإعلان.
 */
export const CANDIDATE_SOURCES = [
  'SITE',
  'REFERRAL',
  'SOCIAL',
  'AGENCY',
  'WALK_IN',
  'OTHER',
] as const;
export type CandidateSource = (typeof CANDIDATE_SOURCES)[number];

export const CANDIDATE_SOURCE_DEFS: readonly Labelled<CandidateSource>[] = [
  { key: 'SITE', label: 'رابط الوظيفة', kind: 'brand' },
  { key: 'REFERRAL', label: 'ترشيح موظّف', kind: 'success' },
  { key: 'SOCIAL', label: 'مواقع التواصل', kind: 'info' },
  { key: 'AGENCY', label: 'مكتب توظيف', kind: 'warning' },
  { key: 'WALK_IN', label: 'حضور مباشر', kind: 'neutral' },
  { key: 'OTHER', label: 'أخرى', kind: 'neutral' },
] as const;

export const CANDIDATE_SOURCE_MAP = Object.fromEntries(
  CANDIDATE_SOURCE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<CandidateSource, Labelled<CandidateSource>>>;

/* ==========================================================================
   قنوات النشر
   ========================================================================== */

/**
 * أين يُنشر الإعلان.
 *
 * ## ولماذا تُربط كلُّ قناةٍ بمصدرِ مرشّح
 *
 * قائمةُ قنواتٍ تُعلَّم في نموذجٍ ثمّ لا تُقاس هي نيّةٌ لا قياس. فلكلّ قناةٍ
 * هنا مصدرٌ من كتالوج مصادر المرشّحين، ومنه يُولَّد رابطُ تقديمٍ يحمل
 * مصدرَه في عنوانه — فمن قدّم من رابط لينكدإن يُسجَّل مصدرُه «مواقع
 * التواصل» بلا أن يُسأل وبلا أن يُخمَّن.
 *
 * وهذا كلُّ ما يُمكن قياسُه بصدق: لا تكاملَ مع أيّ منصّةٍ ولا قراءةَ
 * مشاهداتٍ منها — الرابطُ هو الجسرُ الوحيد.
 */
export const HIRING_CHANNELS = [
  'SITE',
  'LINKEDIN',
  'FACEBOOK',
  'WHATSAPP',
  'REFERRAL',
  'AGENCY',
  'FAIR',
  'PAID_ADS',
  'OTHER',
] as const;
export type HiringChannel = (typeof HIRING_CHANNELS)[number];

export interface HiringChannelDef extends Labelled<HiringChannel> {
  description: string;
  /** مصدرُ المرشّح الذي يُسجَّل لمن جاء منها. */
  source: CandidateSource;
}

export const HIRING_CHANNEL_DEFS: readonly HiringChannelDef[] = [
  {
    key: 'SITE',
    label: 'موقع الشركة',
    kind: 'brand',
    description: 'الرابط منشورٌ في صفحة الوظائف على موقعكم.',
    source: 'SITE',
  },
  {
    key: 'LINKEDIN',
    label: 'لينكدإن',
    kind: 'info',
    description: 'منشورٌ على صفحة الشركة أو حساب من يوظّف.',
    source: 'SOCIAL',
  },
  {
    key: 'FACEBOOK',
    label: 'فيسبوك',
    kind: 'info',
    description: 'منشورٌ على الصفحة أو في مجموعات التوظيف.',
    source: 'SOCIAL',
  },
  {
    key: 'WHATSAPP',
    label: 'مجموعات واتساب',
    kind: 'success',
    description: 'يُرسَل الرابطُ في مجموعاتٍ مهنيّة.',
    source: 'SOCIAL',
  },
  {
    key: 'REFERRAL',
    label: 'ترشيح الموظّفين',
    kind: 'success',
    description: 'يُوزَّع الرابطُ على الفريق ليرشّحوا من يعرفون.',
    source: 'REFERRAL',
  },
  {
    key: 'AGENCY',
    label: 'مكاتب التوظيف',
    kind: 'warning',
    description: 'يُسلَّم الرابطُ لمكتبٍ يرشّح مقابل أجر.',
    source: 'AGENCY',
  },
  {
    key: 'FAIR',
    label: 'معارض التوظيف',
    kind: 'neutral',
    description: 'رمزُ استجابةٍ على الطاولة أو رابطٌ يُملى.',
    source: 'WALK_IN',
  },
  {
    key: 'PAID_ADS',
    label: 'إعلان مدفوع',
    kind: 'warning',
    description: 'إعلانٌ بميزانيةٍ على منصّةٍ إعلانيّة.',
    source: 'SOCIAL',
  },
  {
    key: 'OTHER',
    label: 'أخرى',
    kind: 'neutral',
    description: 'قناةٌ لا اسمَ لها هنا — تُكتب في تفاصيل الحملة.',
    source: 'OTHER',
  },
] as const;

export const HIRING_CHANNEL_MAP = Object.fromEntries(
  HIRING_CHANNEL_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<HiringChannel, HiringChannelDef>>;

/** ما تُقرأ به قناةٌ محفوظة — وتُسقَط ما لم تعد في الكتالوج. */
export function readChannels(value: unknown): HiringChannel[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (c): c is HiringChannel => typeof c === 'string' && c in HIRING_CHANNEL_MAP,
  );
}

/* ==========================================================================
   حالة الحملة والوظيفة
   ========================================================================== */

export const HIRING_CAMPAIGN_STATUSES = [
  'PLANNED',
  'RUNNING',
  'DONE',
  'CANCELLED',
] as const;
export type HiringCampaignStatus = (typeof HIRING_CAMPAIGN_STATUSES)[number];

export const HIRING_CAMPAIGN_STATUS_DEFS: readonly Labelled<HiringCampaignStatus>[] =
  [
    { key: 'PLANNED', label: 'مخطَّطة', kind: 'neutral' },
    { key: 'RUNNING', label: 'جارية', kind: 'success' },
    { key: 'DONE', label: 'منتهية', kind: 'info' },
    { key: 'CANCELLED', label: 'ملغاة', kind: 'danger' },
  ] as const;

export const HIRING_CAMPAIGN_STATUS_MAP = Object.fromEntries(
  HIRING_CAMPAIGN_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<HiringCampaignStatus, Labelled<HiringCampaignStatus>>>;

export const JOB_POST_STATUSES = ['DRAFT', 'OPEN', 'PAUSED', 'CLOSED'] as const;
export type JobPostStatus = (typeof JOB_POST_STATUSES)[number];

export interface JobPostStatusDef extends Labelled<JobPostStatus> {
  /** هل الرابط العلني يقبل تقديمًا في هذه الحالة. */
  accepting: boolean;
  description: string;
}

export const JOB_POST_STATUS_DEFS: readonly JobPostStatusDef[] = [
  {
    key: 'DRAFT',
    label: 'مسوّدة',
    kind: 'neutral',
    accepting: false,
    description: 'تُكتب ولم تُنشر. الرابط العلني لا يفتحها.',
  },
  {
    key: 'OPEN',
    label: 'مفتوحة',
    kind: 'success',
    accepting: true,
    description: 'منشورة وتقبل التقديم.',
  },
  {
    key: 'PAUSED',
    label: 'موقوفة',
    kind: 'warning',
    accepting: false,
    description: 'منشورة ولا تقبل تقديمًا جديدًا. الطلبات القائمة تكمل مسارها.',
  },
  {
    key: 'CLOSED',
    label: 'مغلقة',
    kind: 'info',
    accepting: false,
    description: 'انتهت — امتلأ الشاغر أو مضى موعدها.',
  },
] as const;

export const JOB_POST_STATUS_MAP = Object.fromEntries(
  JOB_POST_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<JobPostStatus, JobPostStatusDef>>;

/* ==========================================================================
   حالة الطلب
   ========================================================================== */

/**
 * الحالةُ غيرُ المرحلة.
 *
 * المرحلةُ تقول أين وصل، والحالةُ تقول هل ما زال في السباق. فمن أُدرج في
 * قائمة الانتظار تبقى مرحلتُه كما هي — «وقف عند المقابلة النهائية
 * ومؤهَّلٌ ينتظر شاغرًا» جملةٌ مفيدة، و«قائمة انتظار» وحدها ليست كذلك.
 */
export const APPLICATION_STATUSES = [
  'ACTIVE',
  'HIRED',
  'REJECTED',
  'WITHDRAWN',
  'WAITLIST',
] as const;
export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];

export interface ApplicationStatusDef extends Labelled<ApplicationStatus> {
  /** هل يبقى في مسار الوظيفة أم خرج منه. */
  open: boolean;
}

export const APPLICATION_STATUS_DEFS: readonly ApplicationStatusDef[] = [
  { key: 'ACTIVE', label: 'قائم', kind: 'info', open: true },
  { key: 'HIRED', label: 'عُيّن', kind: 'success', open: false },
  { key: 'REJECTED', label: 'مستبعَد', kind: 'danger', open: false },
  { key: 'WITHDRAWN', label: 'انسحب', kind: 'neutral', open: false },
  { key: 'WAITLIST', label: 'قائمة الانتظار', kind: 'warning', open: false },
] as const;

export const APPLICATION_STATUS_MAP = Object.fromEntries(
  APPLICATION_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<ApplicationStatus, ApplicationStatusDef>>;

/** الحالات التي يبقى صاحبها في المسار. */
export const OPEN_APPLICATION_STATUSES = APPLICATION_STATUS_DEFS.filter(
  (d) => d.open,
).map((d) => d.key) as readonly ApplicationStatus[];

/* ==========================================================================
   المراحل
   ========================================================================== */

export const APPLICATION_STAGES = [
  'APPLIED',
  'SCREENING',
  'FIRST_CALL',
  'INTERVIEW',
  'FINAL',
  'OFFER',
  'HIREABLE',
  'HIRED',
] as const;
export type ApplicationStage = (typeof APPLICATION_STAGES)[number];

export interface ApplicationStageDef extends Labelled<ApplicationStage> {
  /** ترتيبها في المسار — به يُعرف التقدّم من التراجع. */
  order: number;
  description: string;
  /** ما يُنتظر فعلُه هنا، يُعرض على البطاقة. */
  action: string | null;
}

export const APPLICATION_STAGE_DEFS: readonly ApplicationStageDef[] = [
  {
    key: 'APPLIED',
    label: 'تقديم',
    kind: 'neutral',
    order: 1,
    description: 'وصل الطلب ولم يفتحه أحد بعد.',
    action: 'افتح الملفّ واقرأه',
  },
  {
    key: 'SCREENING',
    label: 'فرز',
    kind: 'info',
    order: 2,
    description: 'قُرئ الملفّ ويُقارَن بالمطلوب.',
    action: 'قرّر: يُكلَّم أم يُستبعَد',
  },
  {
    key: 'FIRST_CALL',
    label: 'مكالمة أولى',
    kind: 'info',
    order: 3,
    description: 'اتّصالٌ قصير: الجدّية والراتب والإتاحة.',
    action: 'اتّصل وسجّل ما دار',
  },
  {
    key: 'INTERVIEW',
    label: 'مقابلة',
    kind: 'brand',
    order: 4,
    description: 'مقابلةٌ مع المسؤول المباشر، حضورًا أو أونلاين.',
    action: 'حدّد موعدًا',
  },
  {
    key: 'FINAL',
    label: 'مقابلة نهائية',
    kind: 'brand',
    order: 5,
    description: 'المقابلة الأخيرة قبل القرار.',
    action: 'حدّد موعدًا',
  },
  {
    key: 'OFFER',
    label: 'عرض عمل',
    kind: 'warning',
    order: 6,
    description: 'صدر العرض ويُنتظر ردُّه.',
    action: 'أصدر العرض وأرسله',
  },
  {
    /*
     * قَبِل العرضَ ولم يُفتح ملفُّه بعد.
     *
     * مرحلةٌ قائمةٌ بذاتها لا لحظةٌ عابرة: بين القبول وفتح الملفّ تقع
     * موافقةُ الموارد البشرية وتُجمع الأوراقُ ويُتّفق على أوّل يوم. وكان
     * القبولُ يُبقيه في «عرض عمل» فلا يُعرف من ينتظر ردًّا ممّن ردّ
     * وينتظر ملفًّا — وهما صنفان مختلفان، ولكلٍّ عملٌ آخر.
     */
    key: 'HIREABLE',
    label: 'قابل للتعيين',
    kind: 'warning',
    order: 7,
    description: 'قَبِل العرضَ ويُنتظر فتحُ ملفّه الوظيفيّ.',
    action: 'افتح ملفَّه الوظيفيّ',
  },
  {
    key: 'HIRED',
    label: 'تعيين',
    kind: 'success',
    order: 8,
    description: 'فُتح له ملفٌّ وظيفيٌّ وانتقل إلى شؤون العاملين.',
    action: null,
  },
] as const;

export const APPLICATION_STAGE_MAP = Object.fromEntries(
  APPLICATION_STAGE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<ApplicationStage, ApplicationStageDef>>;

/*
 * لا دالّتين تقرآن هذا الكتالوج.
 *
 * كانت `nextStage` و`stageMoveKind` تقرآن الترتيبَ من هنا، فصارتا
 * `nextStageOf` و`stageMoveKindOf` أدناه — تأخذان أعمدةَ الشركة كما
 * ضبطتها. والقديمتان لم تُتركا مهجورتين: دالّةٌ تقرأ كتالوجًا لم يعد هو
 * المصدر تُستعمل يومًا بحسن نيّةٍ فتُنتج جوابًا عن مسارٍ لا وجودَ له.
 *
 * والتراجعُ مسموحٌ في الاثنتين ولا يُمنع: مقابلةٌ تكشف أنّ الفرز كان
 * متسرّعًا، والنظامُ الذي يمنع الرجوعَ يجعل المستخدمَ يستبعد الطلبَ ويفتح
 * غيرَه — فيضيع التاريخ.
 */

/* ==========================================================================
   أعمدة المسار كما تضبطها الشركة
   ========================================================================== */

/**
 * المراحلُ السبعُ أعلاه هي البذرةُ والأساس.
 *
 * تُزرع لكلّ شركةٍ عند أوّل فتحٍ للمسار، ولها بعد ذلك ما لغيرها: تُعاد
 * تسميتُها وترتيبُها ويُوقَف عرضُها. ولا تُحذف ولا يُغيَّر مفتاحُها، لأنّ
 * مفتاحين منها لهما معنًى في الشيفرة لا في العرض.
 */
export const CORE_STAGE_KEYS: readonly ApplicationStage[] = APPLICATION_STAGES;

/**
 * المرحلتان اللتان لا يستقيم النظامُ بدونهما.
 *
 * «عرض عمل» هي التي يُصدَر فيها العرضُ ويُنتظر ردُّه، و«تعيين» هي التي
 * يُفتح عندها ملفٌّ وظيفيّ ويُنقل الملفُّ إلى شؤون العاملين. وإيقافُ
 * إحداهما يقطع الطريقَ بين المرشّح والموظّف — فيُمنع، ويُقال لماذا.
 */
export const REQUIRED_STAGE_KEYS = ['OFFER', 'HIREABLE', 'HIRED'] as const;

/** أقصى عددِ أعمدةٍ في المسار — ما زاد لا تُرى نهايتُه على شاشة. */
export const MAX_PIPELINE_STAGES = 14;

/** شكلُ العمود كما يردّه الخادم — يُقرأ في الشاشة وفي حساب الحركة. */
export interface StageShape {
  key: string;
  label: string;
  order: number;
}

/**
 * صيغةُ مفتاحِ مرحلةٍ جديدة.
 *
 * حروفٌ لاتينيّةٌ كبيرةٌ وأرقامٌ وشرطةٌ سفليّة: المفتاحُ يُكتب في عمود
 * نصٍّ ويُقارَن ويُرشَّح به، ومفتاحٌ عربيٌّ أو فيه مسافةٌ يصير في عناوين
 * الروابط سلسلةَ رموزٍ لا تُقرأ. والاسمُ المعروضُ عربيٌّ حرٌّ — وهو ما
 * يراه الناس.
 */
export const STAGE_KEY_PATTERN = /^[A-Z][A-Z0-9_]{1,30}$/;

/** مفتاحٌ مقترَحٌ من اسمٍ عربيّ — يُعرض ويُعدَّل، ولا يُفرض. */
export function suggestStageKey(label: string, taken: readonly string[]): string {
  const latin = label
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  /*
   * الاسمُ العربيُّ لا يُنتج حروفًا لاتينيّة، فيُردّ إلى «STAGE».
   *
   * ولا يُحرَّف بالنقل الصوتيّ: «مقابلة» تصير «MQABLH» أو «MUQABALA» بحسب
   * من ينقل، ومفتاحٌ كهذا لا يقرؤه أحدٌ ولا يُخمَّن.
   */
  const base = STAGE_KEY_PATTERN.test(latin) ? latin : 'STAGE';
  if (!taken.includes(base)) return base;
  for (let i = 2; i < 100; i++) {
    const next = `${base}_${i}`;
    if (!taken.includes(next)) return next;
  }
  return `${base}_${Date.now().toString(36).toUpperCase().slice(-4)}`;
}

/**
 * وصفُ حركةٍ بين مرحلتين من أعمدةٍ تُقرأ وقتَ التشغيل.
 *
 * النسخةُ الثابتةُ أعلاه تقرأ الكتالوج، وهذه تقرأ ما ضبطته الشركة —
 * ومرحلةٌ أضافتها بين الفرز والمقابلة تجعل الانتقالَ إليها «تقدّمًا» عند
 * هذه ولا معنى لها عند تلك.
 */
export function stageMoveKindOf(
  stages: readonly StageShape[],
  from: string,
  to: string,
): 'forward' | 'back' | 'same' {
  const a = stages.find((s) => s.key === from)?.order;
  const b = stages.find((s) => s.key === to)?.order;
  /* مرحلةٌ لا تُعرف لا تُقاس: تُعامَل كحركةٍ في المكان فلا تُطلق شرطًا. */
  if (a === undefined || b === undefined) return 'same';
  if (b > a) return 'forward';
  if (b < a) return 'back';
  return 'same';
}

/** المرحلةُ التالية في مسارِ شركةٍ بعينها، أو نفسُها إن كانت الأخيرة. */
export function nextStageOf(
  stages: readonly StageShape[],
  from: string,
): string {
  const sorted = [...stages].sort((a, b) => a.order - b.order);
  const at = sorted.findIndex((s) => s.key === from);
  if (at < 0 || at === sorted.length - 1) return from;
  return sorted[at + 1]!.key;
}

/* ==========================================================================
   سجلّ الطلب
   ========================================================================== */

export const APPLICATION_EVENT_KINDS = [
  'APPLIED',
  'STAGE',
  'STATUS',
  'NOTE',
  'INTERVIEW',
  'EVALUATION',
  'OFFER',
] as const;
export type ApplicationEventKind = (typeof APPLICATION_EVENT_KINDS)[number];

export const APPLICATION_EVENT_KIND_MAP: Readonly<
  Record<ApplicationEventKind, string>
> = {
  APPLIED: 'تقدَّم',
  STAGE: 'انتقل',
  STATUS: 'تغيّرت حالته',
  NOTE: 'ملاحظة',
  INTERVIEW: 'موعد لقاء',
  EVALUATION: 'تقييم',
  OFFER: 'عرض عمل',
};

/* ==========================================================================
   عرض العمل
   ========================================================================== */

export const JOB_OFFER_STATUSES = [
  'DRAFT',
  'SENT',
  'ACCEPTED',
  'DECLINED',
  'EXPIRED',
  'WITHDRAWN',
] as const;
export type JobOfferStatus = (typeof JOB_OFFER_STATUSES)[number];

export interface JobOfferStatusDef extends Labelled<JobOfferStatus> {
  description: string;
  /** هل الرابط العلنيّ يقبل ردًّا في هذه الحالة. */
  answerable: boolean;
  /** هل العرض ما زال يحجز الشاغر. */
  live: boolean;
}

export const JOB_OFFER_STATUS_DEFS: readonly JobOfferStatusDef[] = [
  {
    key: 'DRAFT',
    label: 'مسوّدة',
    kind: 'neutral',
    description: 'يُكتب ويُراجَع. لم يره المرشّح بعد.',
    answerable: false,
    live: true,
  },
  {
    key: 'SENT',
    label: 'أُرسل',
    kind: 'info',
    description: 'عند المرشّح ويُنتظر ردُّه.',
    answerable: true,
    live: true,
  },
  {
    key: 'ACCEPTED',
    label: 'قَبِل',
    kind: 'success',
    description: 'قَبِل باسمه وتاريخه — ويُفتح له ملفٌّ وظيفيّ.',
    answerable: false,
    live: true,
  },
  {
    key: 'DECLINED',
    label: 'رفض',
    kind: 'danger',
    description: 'ردّ بالرفض. والسببُ إن كتبه يُقرأ في العرض القادم.',
    answerable: false,
    live: false,
  },
  {
    key: 'EXPIRED',
    label: 'انتهت مهلته',
    kind: 'warning',
    description: 'مضى موعد الردّ ولم يردّ.',
    answerable: false,
    live: false,
  },
  {
    key: 'WITHDRAWN',
    label: 'سُحب',
    kind: 'neutral',
    description: 'سُحب من عندنا قبل الردّ.',
    answerable: false,
    live: false,
  },
] as const;

export const JOB_OFFER_STATUS_MAP = Object.fromEntries(
  JOB_OFFER_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<JobOfferStatus, JobOfferStatusDef>>;

/** رمزُ رابط العرض: طويلٌ لأنّ من يخمّنه يقبل عرضًا باسم غيره. */
export const OFFER_TOKEN_PATTERN = /^[a-z0-9]{24,48}$/;

/**
 * رابطُ العرض كما يُنسخ ويُرسل.
 *
 * يُبنى في مكانٍ واحدٍ لأنّه يُكتب في ثلاثة: الشاشةُ التي تنسخه، والرسالةُ
 * التي تُرسله، والصفحةُ التي تقرؤه.
 */
export function jobOfferUrl(origin: string, token: string): string {
  return `${origin.replace(/\/+$/, '')}/offer/${token}`;
}

/**
 * صياغةُ خطاب العرض.
 *
 * تُولَّد مرّةً عند الإنشاء ثمّ تُحرَّر ولا تُعاد: عرضٌ يُعاد توليدُه بعد
 * تحريره يمحو ما كتبه من راجعه. والصياغةُ هنا لا في الخادم لأنّ المعاينة
 * تحتاجها قبل الحفظ.
 */
export function draftOfferBody(input: {
  candidateName: string;
  companyName: string;
  title: string;
  department?: string | null;
  employmentLabel: string;
  startDate: string;
  salaryText: string;
  probationMonths: number;
  benefits?: string | null;
}): string {
  const lines = [
    `الأستاذ/ة ${input.candidateName} المحترم/ة،`,
    '',
    `يسرّ ${input.companyName} أن تعرض عليكم الانضمام إلى فريقها في وظيفة «${input.title}»${
      input.department ? ` بقسم ${input.department}` : ''
    }، بنظام ${input.employmentLabel}.`,
    '',
    `تاريخ المباشرة: ${input.startDate}.`,
    `الراتب الأساسي: ${input.salaryText} شهريًّا.`,
    input.probationMonths > 0
      ? `فترة الاختبار: ${input.probationMonths} أشهر من تاريخ المباشرة.`
      : 'لا فترة اختبار.',
  ];

  if (input.benefits) {
    lines.push('', 'المزايا:', input.benefits);
  }

  lines.push(
    '',
    'ويسري هذا العرض بقبولكم عليه، ويُستكمل بعده عقدُ العمل وإجراءاتُ التعيين.',
    '',
    'وتفضّلوا بقبول وافر الاحترام،',
    input.companyName,
  );

  return lines.join('\n');
}

/* ==========================================================================
   الحدود
   ========================================================================== */

/**
 * سقف حقول الاستمارة.
 *
 * أطول من سقف الحقول المخصّصة عمدًا: استمارةُ التقديم تُملأ مرّةً واحدةً
 * ويقبل صاحبُها طولَها، بخلاف نموذجٍ يفتحه الموظّف عشر مرّات في اليوم.
 * وما فوق ذلك استمارةٌ يهجرها نصفُ من يبدؤها.
 */
export const MAX_FORM_FIELDS = 40;

/** أقصى ما يُقبل من طلبات على رابطٍ واحد في اليوم. */
export const MAX_APPLICATIONS_PER_DAY = 300;

/** الجزء العلني من رابط الوظيفة: حروفٌ وأرقامٌ لا تحمل معنًى يُخمَّن. */
export const JOB_SLUG_PATTERN = /^[a-z0-9]{8,24}$/;

/**
 * رابط التقديم كما يُنسخ ويُنشر.
 *
 * يُبنى في مكانٍ واحدٍ لأنّه يُكتب في ثلاثة: الشاشة التي تنسخه، والرسالة
 * التي تُرسله، والصفحة التي تقرؤه. وثلاثةُ نسخٍ من قالبٍ واحدٍ تتباعد.
 */
/**
 * رابطُ صفحة وظائف الشركة.
 *
 * تحت بادئة التقديم نفسِها: رابطٌ واحدٌ يُحفظ ويُنشر، وما تحته وظيفةٌ
 * بعينها. ولا يُشتقّ منه شيءٌ يتغيّر — اسمُ الشركة في النطاق ثابت.
 */
export function careersUrl(origin: string, tenantSlug: string): string {
  return `${origin.replace(/\/+$/, '')}/apply/${tenantSlug}`;
}

export function jobApplyUrl(
  origin: string,
  tenantSlug: string,
  slug: string,
  /**
   * القناةُ التي نُشر فيها هذا الرابط.
   *
   * تُكتب في العنوان لا تُسأل من المتقدّم: سؤالُ «من أين عرفتَ عنّا؟» في
   * استمارةٍ يُجاب عنه بالتخمين أو يُترك، وقياسُ قناةٍ على إجابةٍ كهذه
   * يُنفق ميزانيةً على أرقامٍ مخترعة. والرابطُ يحمل مصدرَه فيُسجَّل بلا
   * سؤال.
   */
  channel?: HiringChannel | null,
): string {
  const base = `${origin.replace(/\/+$/, '')}/apply/${tenantSlug}/${slug}`;
  if (!channel || channel === 'SITE') return base;
  return `${base}?c=${channel}`;
}
